// MeetingRoomDetailModal.jsx - Updated with enhanced gallery and image enlargement
import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  Grid,
  Chip,
  Button,
  Paper,
  MobileStepper,
  Divider,
  Modal,
  Backdrop,
  Fade
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import PeopleIcon from '@mui/icons-material/People';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ZoomOutMapIcon from '@mui/icons-material/ZoomOutMap';
import { API_BASE_URL } from 'config/api';
import * as Icons from '@mui/icons-material';

const NavigationButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: 'rgba(255, 255, 255, 0.7)',
  color: theme.palette.grey[800],
  position: 'absolute',
  padding: 8,
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
  },
  zIndex: 1,
}));

const ZoomButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: 'rgba(255, 255, 255, 0.7)',
  color: theme.palette.grey[800],
  position: 'absolute',
  padding: 8,
  top: 8,
  right: 8,
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
  },
  zIndex: 1,
}));

const EnlargedImageContainer = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  outline: 'none',
  width: '90vw',
  height: '90vh',
  maxWidth: '1200px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
}));

const EnlargedImage = styled('img')(({ theme }) => ({
  maxWidth: '100%',
  maxHeight: '85vh',
  objectFit: 'contain',
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[10],
}));

const MeetingRoomDetailModal = ({
  open,
  onClose,
  room,
  onSelectRoom = null,
  theme
}) => {
  // State for gallery navigation
  const [activeStep, setActiveStep] = useState(0);
  // State for enlarged image
  const [enlargedView, setEnlargedView] = useState(false);

  if (!room) {
    return null;
  }

  // Handle gallery images
  const images = room.images || [];
  // Fallback to single image if gallery is not available
  if (images.length === 0 && room.image) {
    images.push({ url: room.image, name: room.name });
  }

  const maxSteps = images.length;

  // Gallery navigation handlers - enhanced version
  const handleNext = (e) => {
    if (e) e.stopPropagation(); // Prevent bubbling if event exists
    setActiveStep((prevActiveStep) => (prevActiveStep + 1) % maxSteps);
  };

  const handleBack = (e) => {
    if (e) e.stopPropagation(); // Prevent bubbling if event exists
    setActiveStep((prevActiveStep) => (prevActiveStep - 1 + maxSteps) % maxSteps);
  };

  // Handlers for enlarged image view
  const handleOpenEnlargedView = (e) => {
    e.stopPropagation();
    setEnlargedView(true);
  };

  const handleCloseEnlargedView = () => {
    setEnlargedView(false);
  };

  // Handler for selecting this room
  const handleSelectRoom = () => {
    if (onSelectRoom) {
      onSelectRoom(room);
      onClose();
    }
  };

  // Helper function to get icon component from service name
  const getIconNameFromService = (serviceName) => {
    // Map common service names to appropriate Material-UI icon names
    const serviceIconMap = {
      // Access
      "24/7 Access": "AccessTime",
      "24 Hour Access": "AccessTime",
      "Access": "VpnKey",

      // Seating & Furniture
      "Comfortable Seating": "Weekend",
      "Ergonomic Chairs": "Chair",
      "Standing Desks": "TableBar",

      // Amenities
      "Pet Friendly": "Pets",
      "Air Conditioning": "AcUnit",
      "Heating": "Fireplace",
      "Kitchen": "Kitchen",
      "Coffee": "Coffee",
      "Tea": "EmojiFoodBeverage",
      "Water": "Water",
      "Snacks": "LocalCafe",

      // Tech
      "High Speed Internet": "Wifi",
      "WiFi": "Wifi",
      "Projector": "VideoSettings",
      "TV": "Tv",
      "Video Conferencing": "Videocam",
      "Whiteboard": "Edit",

      // Services
      "On-Site Support": "SupportAgent",
      "Reception": "RecordVoiceOver",
      "Cleaning": "CleaningServices",
      "Mail Handling": "Mail",
      "Printing": "Print",
      "Security": "Security",

      // Facilities
      "Bike Storage": "DirectionsBike",
      "Parking": "LocalParking",
      "Shower": "Shower",
      "Restrooms": "Wc",
      "Accessibility": "Accessible",

      // Default
      "default": "CheckCircle"
    };

    // Normalize the service name for comparison (remove spaces, lowercase)
    const normalizedName = serviceName
      .toLowerCase()
      .replace(/\s+/g, ' ')
      .trim();

    // Try to find an exact match
    for (const [key, value] of Object.entries(serviceIconMap)) {
      if (key.toLowerCase() === normalizedName) {
        return value;
      }
    }

    // If no exact match, try to find a partial match
    for (const [key, value] of Object.entries(serviceIconMap)) {
      if (normalizedName.includes(key.toLowerCase())) {
        return value;
      }
    }

    // If all else fails, return the default icon
    return serviceIconMap.default;
  };

  const getIconComponent = (iconName) => {
    return Icons[iconName] || Icons.CheckCircle;
  };

  // Get pricing data
  const hourlyRate = room.hourly_rate;
  const fullDayPrice = room.full_day_price;
  const morningHalfDayPrice = room.morning_half_day_price;
  const afternoonHalfDayPrice = room.afternoon_half_day_price;

  // Use morning half day price if it's available, otherwise afternoon
  const halfDayPrice = morningHalfDayPrice || afternoonHalfDayPrice;

  // Get real services/features
  const services = room.services || [];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottom: '1px solid',
        borderColor: 'divider',
        pb: 1
      }}>
        <Typography variant="h6">{room.name}</Typography>
        <IconButton edge="end" color="inherit" onClick={onClose} aria-label="close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ py: 3 }}>
        <Grid container spacing={3}>
          {/* Room information with enhanced image gallery */}
          <Grid item xs={12} md={7}>
            {/* Image Gallery - Enhanced with chevron navigation */}
            <Box sx={{ position: 'relative', width: '100%', height: 280, mb: 2 }}>
              {maxSteps > 0 ? (
                <>
                  {/* Gallery image */}
                  <Box
                    sx={{
                      height: '100%',
                      display: 'block',
                      overflow: 'hidden',
                      width: '100%',
                      borderRadius: 1,
                      position: 'relative',
                      cursor: 'pointer',
                    }}
                    onClick={handleOpenEnlargedView}
                  >
                    <img
                      src={`${API_BASE_URL}${images[activeStep].url}`}
                      alt={images[activeStep].name || room.name}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        display: 'block'
                      }}
                    />
                    
                    {/* Zoom indicator */}
                    <ZoomButton 
                      size="small"
                      aria-label="enlarge image"
                    >
                      <ZoomOutMapIcon fontSize="small" />
                    </ZoomButton>
                    
                    {/* Chevron navigation buttons - only show if there are multiple images */}
                    {maxSteps > 1 && (
                      <>
                        <NavigationButton 
                          size="small" 
                          onClick={handleBack}
                          sx={{ left: 8, top: '50%', transform: 'translateY(-50%)' }}
                        >
                          <ChevronLeftIcon />
                        </NavigationButton>
                        
                        <NavigationButton 
                          size="small" 
                          onClick={handleNext}
                          sx={{ right: 8, top: '50%', transform: 'translateY(-50%)' }}
                        >
                          <ChevronRightIcon />
                        </NavigationButton>
                      </>
                    )}
                    
                    {/* Mobile stepper for dots navigation */}
                    {maxSteps > 1 && (
                      <MobileStepper
                        steps={maxSteps}
                        position="static"
                        activeStep={activeStep}
                        sx={{
                          position: 'absolute',
                          bottom: 0,
                          width: '100%',
                          background: 'rgba(0,0,0,0.4)',
                          '& .MuiMobileStepper-dot': {
                            backgroundColor: 'rgba(255,255,255,0.5)',
                          },
                          '& .MuiMobileStepper-dotActive': {
                            backgroundColor: 'white',
                          }
                        }}
                        variant="dots"
                        backButton={null}
                        nextButton={null}
                      />
                    )}
                  </Box>
                </>
              ) : (
                <Box
                  sx={{
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'grey.100',
                    borderRadius: 1,
                  }}
                >
                  <MeetingRoomIcon sx={{ fontSize: 60, color: 'grey.400' }} />
                </Box>
              )}
            </Box>

            {/* Room description */}
            {room.description && (
              <Paper
                variant="outlined"
                sx={{ p: 2, mb: 3, bgcolor: 'background.paper' }}
              >
                <Typography variant="body2" color="text.secondary">
                  {room.description}
                </Typography>
              </Paper>
            )}

            {/* Features */}
            {services && services.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                  Features
                </Typography>
                <Grid container spacing={1}>
                  {services.map((service, idx) => {
                    const iconName = getIconNameFromService(service.name);
                    const IconComponent = getIconComponent(iconName);

                    return (
                      <Grid item xs={12} sm={6} key={service.id || idx}>
                        <Box sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          py: 0.5
                        }}>
                          <IconComponent fontSize="small" />
                          <Typography variant="body2">
                            {service.name}
                          </Typography>
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            )}
          </Grid>

          {/* Room summary and pricing */}
          <Grid item xs={12} md={5}>
            <Paper
              elevation={0}
              variant="outlined"
              sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}
            >
              {/* Select button - Moved to the top */}
              <Button
                variant="contained"
                color="primary"
                fullWidth
                size="large"
                onClick={handleSelectRoom}
                disabled={!onSelectRoom}
                sx={{ mb: 3 }}
              >
                Select This Room
              </Button>

              <Typography variant="h6" gutterBottom>
                Room Summary
              </Typography>

              <Box sx={{ mb: 3 }}>
                <Chip
                  icon={<PeopleIcon />}
                  label={`Capacity: ${room.capacity || 1} ${room.capacity === 1 ? 'person' : 'people'}`}
                  size="medium"
                  sx={{ mr: 1, mb: 1 }}
                />
                {room.floorName && (
                  <Chip
                    icon={<MeetingRoomIcon />}
                    label={room.floorName}
                    size="medium"
                    sx={{ mr: 1, mb: 1 }}
                  />
                )}
              </Box>

              {(hourlyRate || halfDayPrice || fullDayPrice) && (
                <>
                  <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                    Pricing
                  </Typography>

                  <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1.5,
                    bgcolor: 'grey.50',
                    p: 2,
                    borderRadius: 1,
                    mb: 3
                  }}>
                    {hourlyRate && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" color="text.secondary">Hourly Rate</Typography>
                        <Typography variant="subtitle1" fontWeight="bold">£{hourlyRate}</Typography>
                      </Box>
                    )}

                    {halfDayPrice && (
                      <>
                        <Divider />
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="body2" color="text.secondary">Half Day (4 hours)</Typography>
                          <Typography variant="subtitle1" fontWeight="bold">£{halfDayPrice}</Typography>
                        </Box>
                      </>
                    )}

                    {fullDayPrice && (
                      <>
                        <Divider />
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="body2" color="text.secondary">Full Day (8 hours)</Typography>
                          <Typography variant="subtitle1" fontWeight="bold">£{fullDayPrice}</Typography>
                        </Box>
                      </>
                    )}
                  </Box>
                </>
              )}
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>
      
      {/* Enlarged Image Modal */}
      <Modal
        open={enlargedView}
        onClose={handleCloseEnlargedView}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
        }}
      >
        <Fade in={enlargedView}>
          <EnlargedImageContainer>
            <Box sx={{ position: 'relative', width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <EnlargedImage
                src={images[activeStep]?.url ? `${API_BASE_URL}${images[activeStep].url}` : ''}
                alt={images[activeStep]?.name || room.name || 'Room image'}
              />
              
              {/* Close button */}
              <IconButton 
                sx={{ 
                  position: 'absolute', 
                  top: 0, 
                  right: 0, 
                  backgroundColor: 'rgba(0,0,0,0.5)', 
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'rgba(0,0,0,0.7)',
                  }
                }}
                onClick={handleCloseEnlargedView}
              >
                <CloseIcon />
              </IconButton>
              
              {/* Navigation buttons in enlarged view */}
              {maxSteps > 1 && (
                <>
                  <NavigationButton
                    onClick={handleBack}
                    sx={{ 
                      left: 16, 
                      top: '50%', 
                      transform: 'translateY(-50%)',
                      backgroundColor: 'rgba(0,0,0,0.5)', 
                      color: 'white',
                      '&:hover': {
                        backgroundColor: 'rgba(0,0,0,0.7)',
                      }
                    }}
                  >
                    <ChevronLeftIcon fontSize="large" />
                  </NavigationButton>
                  
                  <NavigationButton
                    onClick={handleNext}
                    sx={{ 
                      right: 16, 
                      top: '50%', 
                      transform: 'translateY(-50%)',
                      backgroundColor: 'rgba(0,0,0,0.5)', 
                      color: 'white',
                      '&:hover': {
                        backgroundColor: 'rgba(0,0,0,0.7)',
                      }
                    }}
                  >
                    <ChevronRightIcon fontSize="large" />
                  </NavigationButton>
                </>
              )}
            </Box>
          </EnlargedImageContainer>
        </Fade>
      </Modal>
    </Dialog>
  );
};

export default MeetingRoomDetailModal;